/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import { makeStyles } from 'tss-react/mui';
import { Theme } from '@mui/material/styles';

/**
 * Styles for the Map view component
 *
 * @author Julien Tietke <j.tietke@tj-labs.de>
 */
const MapViewStyles = makeStyles()((theme: Theme) => ({
	container: {
		width: '100%',
		padding: theme.spacing(7, 0, 0, 4),
		minWidth: '750px',
	},

	mapContainer: {
		width: '100%',
		maxWidth: '1200px',
		height: '70vh',
		minHeight: '500px',
		borderRadius: '8px',
		overflow: 'hidden',
		boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
		border: `1px solid ${theme.palette.divider}`,
	},

	closeButton: {
		color: '#666',
		cursor: 'pointer',
		transition: 'color 0.2s',
		'&:hover': {
			color: '#000',
		},
	},

	loadingContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		height: '70vh',
		minHeight: '500px',
	},

	infoBadge: {
		position: 'absolute',
		top: theme.spacing(8),
		left: theme.spacing(1),
		backgroundColor: theme.palette.background.paper,
		padding: theme.spacing(1, 2),
		borderRadius: theme.spacing(1),
		boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
		zIndex: 1000,
		'& .MuiTypography-body2': {
			fontFamily: theme.typography.fontFamilyBold,
			color: theme.palette.text.primary,
			fontSize: '13px',
		},
	},

	noEntriesBadge: {
		position: 'absolute',
		top: theme.spacing(8),
		left: theme.spacing(1),
		backgroundColor: theme.palette.background.paper,
		padding: theme.spacing(1.5, 2.5),
		borderRadius: theme.spacing(1),
		boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
		zIndex: 1000,
	},

	noEntriesText: {
		fontFamily: theme.typography.fontFamily,
		color: theme.palette.text.secondary,
		fontSize: '13px',
	},

	actionButtons: {
		display: 'flex',
		justifyContent: 'end',
		marginBottom: theme.spacing(2),
		gap: theme.spacing(1),
		zIndex: 1000,
	},

	actionButton: {
		textTransform: 'none',
	},

	errorContainer: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		alignItems: 'center',
		height: '70vh',
		minHeight: '500px',
		gap: theme.spacing(2),
	},

	errorText: {
		color: theme.palette.error.main,
		fontFamily: theme.typography.fontFamily,
	},

	marker: {
		cursor: 'pointer',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},

	markerIcon: {
		width: '34px',
		height: '40px',
		pointerEvents: 'none',
		transform: 'translateY(-8px)',
	},

	markerIconCluster: {
		width: '40px',
		height: '46px',
		pointerEvents: 'none',
		transform: 'translateY(-10px)',
	},

	userLocationMarker: {
		position: 'relative',
		width: '30px',
		height: '30px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},

	userLocationPulse: {
		position: 'absolute',
		width: '30px',
		height: '30px',
		borderRadius: '50%',
		backgroundColor: theme.palette.primary.main,
		opacity: 0.25,
		animation: 'pulse 1.5s infinite',
	},

	userLocationDot: {
		width: '12px',
		height: '12px',
		borderRadius: '50%',
		backgroundColor: theme.palette.primary.main,
		border: `3px solid ${theme.palette.common.white}`,
		boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
	},

	'@keyframes pulse': {
		'0%': {
			transform: 'scale(0.6)',
			opacity: 0.5,
		},
		'100%': {
			transform: 'scale(1.2)',
			opacity: 0,
		},
	},

	popupContent: {
		padding: theme.spacing(1),
		minWidth: '200px',
		maxWidth: '300px',
	},

	popupHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: theme.spacing(1),
	},

	popupTitle: {
		fontFamily: theme.typography.fontFamilyBold,
		marginBottom: theme.spacing(1),
		color: theme.palette.text.primary,
		fontSize: '14px',
	},

	popupLink: {
		cursor: 'pointer',
		color: theme.palette.primary.main,
		textDecoration: 'underline',
		'&:hover': {
			color: theme.palette.primary.dark,
			textDecoration: 'none',
		},
	},

	popupCloseButton: {
		marginTop: theme.spacing(-1),
		marginRight: theme.spacing(-1),
		color: theme.palette.text.secondary,
		'& .MuiSvgIcon-root': {
			fontSize: '1.4rem',
		},
	},

	popupImage: {
		width: '100%',
		maxHeight: '150px',
		objectFit: 'cover',
		borderRadius: '4px',
		marginBottom: theme.spacing(1),
	},

	popupNote: {
		marginBottom: theme.spacing(1),
		color: theme.palette.text.secondary,
		fontSize: '12px',
		wordWrap: 'break-word',
	},

	popupMetadata: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginTop: theme.spacing(1),
		paddingTop: theme.spacing(1),
		borderTop: `1px solid ${theme.palette.divider}`,
	},

	popupList: {
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(1),
		maxHeight: '250px',
		overflowY: 'auto',
		marginTop: theme.spacing(1),
	},

	filterMenu: {
		width: '100%',
	},

	filterDialogTitle: {
		paddingBottom: 0,
	},

	filterDialogContent: {
		paddingTop: theme.spacing(2),
	},

	filterDialogActions: {
		padding: theme.spacing(2),
		display: 'flex',
		justifyContent: 'flex-end',
		gap: theme.spacing(1),
	},

	filterTitle: {
		fontFamily: theme.typography.fontFamilyBold,
		marginBottom: theme.spacing(1),
	},

	filterSectionTitle: {
		fontFamily: theme.typography.fontFamilyBold,
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(1),
	},

	filterGroup: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: theme.spacing(0.5),
		width: '100%',
		overflowY: 'auto',
		'& .MuiFormControlLabel-root': {
			width: '100%',
			marginLeft: 0,
		},
	},

	filterEmpty: {
		color: theme.palette.text.secondary,
	},

	filterPackage: {
		marginBottom: theme.spacing(2),
	},

	filterPackageTitle: {
		fontFamily: theme.typography.fontFamilyBold,
		alignSelf: 'center',
	},

	filterPackageHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		width: '100%',
	},

	filterPackageCheckbox: {
		width: '100%',
		marginLeft: 0,
		marginBottom: 0,
		alignItems: 'flex-start',
		'& .MuiFormControlLabel-label': {
			width: '100%',
		},
	},

	filterPackageToggle: {
		marginLeft: theme.spacing(1),
		padding: theme.spacing(0.5),
	},

	filterModuleItem: {
		width: '100%',
		marginLeft: 0,
		paddingLeft: theme.spacing(2),
	},

	filterModuleWrapper: {
		width: '100%',
	},

	filterQuestionList: {
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(1),
		paddingLeft: theme.spacing(4),
		paddingRight: theme.spacing(1),
		paddingBottom: theme.spacing(1),
	},

	filterQuestionItem: {
		backgroundColor: theme.palette.grey[50],
		borderRadius: theme.shape.borderRadius,
		padding: theme.spacing(1),
		border: `1px solid ${theme.palette.grey[200]}`,
	},

	filterQuestionTitle: {
		fontWeight: 600,
		fontSize: '0.75rem',
		marginBottom: theme.spacing(0.5),
		display: 'block',
	},

	filterQuestionOptions: {
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(0.25),
		marginTop: theme.spacing(0.5),
	},

	filterQuestionOptionRow: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(0.5),
	},

	filterQuestionOption: {
		fontSize: '0.75rem',
		color: theme.palette.text.secondary,
		borderRadius: theme.shape.borderRadius,
		backgroundColor: theme.palette.background.paper,
		padding: theme.spacing(0.25, 0.75),
		border: `1px solid ${theme.palette.grey[200]}`,
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		flex: 1,
		minWidth: 0,
	},

	filterQuestionCount: {
		fontWeight: 600,
		color: theme.palette.primary.main,
	},

	expandIcon: {
		transition: theme.transitions.create('transform'),
	},

	expandIconOpen: {
		transform: 'rotate(180deg)',
		transition: theme.transitions.create('transform'),
	},

	groupDialogTitle: {
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		paddingRight: theme.spacing(2),
	},

	groupDialogClose: {
		marginLeft: theme.spacing(1),
	},

	groupDialogContent: {
		paddingTop: theme.spacing(2),
		paddingLeft: theme.spacing(2),
		paddingRight: theme.spacing(2),
	},

	groupDialogCount: {
		fontFamily: theme.typography.fontFamilyBold,
	},

	groupDialogHeading: {
		fontFamily: theme.typography.fontFamilyBold,
	},

	groupDialogSubtitle: {
		color: theme.palette.text.secondary,
	},

	groupDialogList: {
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(2),
	},

	groupDialogItem: {
		display: 'flex',
		gap: theme.spacing(2),
		padding: theme.spacing(1.5),
		borderRadius: theme.shape.borderRadius,
		border: `1px solid ${theme.palette.divider}`,
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: theme.palette.action.hover,
		},
	},

	groupDialogImageWrapper: {
		width: '72px',
		height: '72px',
		borderRadius: theme.shape.borderRadius,
		overflow: 'hidden',
		backgroundColor: theme.palette.action.hover,
		flexShrink: 0,
	},

	groupDialogImage: {
		width: '100%',
		height: '100%',
		objectFit: 'cover',
	},

	groupDialogContentInfo: {
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(0.5),
		width: '100%',
	},

	groupDialogItemTitle: {
		fontFamily: theme.typography.fontFamilyBold,
		color: theme.palette.text.primary,
	},

	groupDialogItemNote: {
		color: theme.palette.text.secondary,
	},

	groupDialogMeta: {
		display: 'flex',
		justifyContent: 'space-between',
		color: theme.palette.text.secondary,
	},

	groupDialogStudent: {
		fontFamily: theme.typography.fontFamilyBold,
	},

	groupDialogDate: {
		fontStyle: 'italic',
	},

	popupListItem: {
		padding: theme.spacing(1),
		borderRadius: theme.spacing(1),
		backgroundColor: theme.palette.grey['50'],
		border: `1px solid ${theme.palette.divider}`,
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(0.5),
	},

	popupListContent: {
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(0.5),
	},

	popupListTitle: {
		fontFamily: theme.typography.fontFamilyBold,
		fontSize: '13px',
	},

	popupListNote: {
		fontSize: '12px',
		color: theme.palette.text.secondary,
		wordWrap: 'break-word',
	},

	popupStudent: {
		color: theme.palette.text.secondary,
		fontSize: '11px',
	},

	popupDate: {
		color: theme.palette.text.secondary,
		fontSize: '11px',
	},
}));

export default MapViewStyles;
